import React from 'react'
import { Button, Space, Typography, Card, Row, Col, Table, Tag, Divider, theme } from 'antd'
import {
  EditOutlined,
  PrinterOutlined,
  MailOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  AlertOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import { formatDateTime } from '../utils/formatters'
import { hexToRGBA } from '../../../../lib/utils'

const { Title, Paragraph } = Typography

const DischargeDetailView = ({ discharge, onClose }) => {
  const { token } = theme.useToken()

  if (!discharge) return null

  // Section header styling function
  const SectionTitle = ({ icon, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
      {icon && React.cloneElement(icon, { style: { marginRight: '8px', color: token.colorPrimary } })}
      <Title level={5} style={{ margin: 0, color: token.colorPrimary }}>
        {children}
      </Title>
    </div>
  )

  return (
    <div className='discharge-view' style={{ marginTop: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          position: 'relative'
        }}
      >
        <Button
          icon={<ArrowLeftOutlined style={{ color: token.colorInfo }} />}
          onClick={onClose}
          style={{
            background: hexToRGBA(token.colorInfo, 0.1),
            borderColor: token.colorInfo,
            color: token.colorInfo,
            fontWeight: 'bold'
          }}
        >
          Back to List
        </Button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          <FileTextOutlined style={{ marginRight: '8px', fontSize: '24px', color: token.colorPrimary }} />
          <Title level={4} style={{ margin: 0, color: token.colorPrimary }}>
            Discharge Instructions
          </Title>
        </div>

        <Space>
          <Button
            icon={<EditOutlined style={{ color: token.colorPrimary }} />}
            style={{
              background: hexToRGBA(token.colorPrimary, 0.1),
              borderColor: token.colorPrimary,
              color: token.colorPrimary,
              fontWeight: 'bold'
            }}
          >
            Edit
          </Button>
          <Button
            icon={<PrinterOutlined style={{ color: token.colorSuccess }} />}
            style={{
              background: hexToRGBA(token.colorSuccess, 0.1),
              borderColor: token.colorSuccess,
              color: token.colorSuccess,
              fontWeight: 'bold'
            }}
          >
            Print
          </Button>
          <Button
            icon={<MailOutlined style={{ color: token.colorWarning }} />}
            style={{
              background: hexToRGBA(token.colorWarning, 0.1),
              borderColor: token.colorWarning,
              color: token.colorWarning,
              fontWeight: 'bold'
            }}
          >
            Email
          </Button>
          <Button
            icon={<FilePdfOutlined style={{ color: token.colorError }} />}
            style={{
              background: hexToRGBA(token.colorError, 0.1),
              borderColor: token.colorError,
              color: token.colorError,
              fontWeight: 'bold'
            }}
          >
            Download PDF
          </Button>
        </Space>
      </div>

      <Card>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <SectionTitle>Patient Information</SectionTitle>
            <Paragraph>
              <strong>Name:</strong> {discharge.patientName}
            </Paragraph>
            <Paragraph>
              <strong>ID:</strong> {discharge.patientId}
            </Paragraph>
            <Paragraph>
              <strong>Admission Date:</strong> {formatDateTime(discharge.admissionDate)}
            </Paragraph>
            <Paragraph>
              <strong>Discharge Date:</strong> {formatDateTime(discharge.dischargeDate)}
            </Paragraph>
          </Col>

          <Col span={12}>
            <SectionTitle>Provider Information</SectionTitle>
            <Paragraph>
              <strong>Attending Physician:</strong> {discharge.attendingPhysician}
            </Paragraph>
            <Paragraph>
              <strong>Department:</strong> {discharge.department}
            </Paragraph>
            {discharge.dischargingSurgeon && (
              <Paragraph>
                <strong>Discharging Surgeon:</strong> {discharge.dischargingSurgeon}
              </Paragraph>
            )}
            <Paragraph>
              <strong>Instructions Created By:</strong> {discharge.createdBy}
            </Paragraph>
          </Col>
        </Row>

        <Divider />

        <SectionTitle>Diagnosis</SectionTitle>
        <div style={{ marginBottom: '16px' }}>
          {discharge.diagnosisList.map((diagnosis, index) => (
            <Tag key={index} color='blue' style={{ margin: '4px' }}>
              {diagnosis}
            </Tag>
          ))}
        </div>

        <SectionTitle icon={<MedicineBoxOutlined />}>Medication Instructions</SectionTitle>
        <Table
          dataSource={discharge.medicationInstructions}
          pagination={false}
          rowKey={(record, index) => `med-${index}`}
          columns={[
            {
              title: 'Medication',
              dataIndex: 'medication',
              key: 'medication'
            },
            {
              title: 'Dosage',
              dataIndex: 'dosage',
              key: 'dosage'
            },
            {
              title: 'Frequency',
              dataIndex: 'frequency',
              key: 'frequency'
            },
            {
              title: 'Duration',
              dataIndex: 'duration',
              key: 'duration'
            },
            {
              title: 'Special Instructions',
              dataIndex: 'specialInstructions',
              key: 'specialInstructions'
            }
          ]}
          style={{ marginBottom: '24px' }}
        />

        <SectionTitle icon={<CalendarOutlined />}>Follow-up Appointments</SectionTitle>
        <Table
          dataSource={discharge.followUpAppointments}
          pagination={false}
          rowKey={(record, index) => `appt-${index}`}
          columns={[
            {
              title: 'Specialist',
              dataIndex: 'specialist',
              key: 'specialist'
            },
            {
              title: 'Date/Time',
              dataIndex: 'date',
              key: 'date',
              render: (date) => formatDateTime(date)
            },
            {
              title: 'Location',
              dataIndex: 'location',
              key: 'location'
            },
            {
              title: 'Purpose',
              dataIndex: 'purpose',
              key: 'purpose'
            }
          ]}
          style={{ marginBottom: '24px' }}
        />

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title={<div style={{ color: token.colorPrimary }}>Activity Restrictions</div>} size='small'>
              <Paragraph>{discharge.activityRestrictions}</Paragraph>
            </Card>
          </Col>

          <Col span={12}>
            <Card title={<div style={{ color: token.colorPrimary }}>Dietary Restrictions</div>} size='small'>
              <Paragraph>{discharge.dietaryRestrictions}</Paragraph>
            </Card>
          </Col>

          {discharge.woundCare && (
            <Col span={24}>
              <Card title={<div style={{ color: token.colorPrimary }}>Wound Care Instructions</div>} size='small'>
                <Paragraph>{discharge.woundCare}</Paragraph>
              </Card>
            </Col>
          )}

          <Col span={24}>
            <Card
              title={
                <div style={{ color: token.colorPrimary }}>
                  <AlertOutlined style={{ marginRight: '8px' }} />
                  Special Notes and Warning Signs
                </div>
              }
              size='small'
            >
              <Paragraph>{discharge.specialNotes}</Paragraph>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default DischargeDetailView
